// Game-like training tour. Every step dims the screen around one target and
// explains one piece of the app:
//   tap   — completes when the highlighted element is clicked
//   input — completes when the highlighted field has at least `minLength` chars
//   text  — completes when the element's text matches `await` (the ride moves
//           on by itself)
//   next  — a pure explanation with a «Дальше» button (no natural action)
// `orGone` covers actions performed by another route (Enter in a field) — the
// step completes when its target disappears. `blurOnNext` closes the keyboard
// before the following step points at the tab bar.
// TUTORIAL_VERSION must match services/trainingService.js: the tour starts by
// itself until the user finishes or skips the version.

export const TUTORIAL_VERSION = 1;

export const TOUR_STEPS = {
  passenger: [
    {
      target: '[data-tour="context-row"]',
      advance: "next",
      text: "Добро пожаловать в «Бомбилу»! Сверху выбираются город и роль, у колокола — push-уведомления о заказах и сообщениях. Обучение идёт на учебном заказе: его видите только вы, реальные водители его не получают.",
    },
    {
      target: '[data-tour="tabbar"]',
      advance: "next",
      text: "Внизу четыре вкладки: «Диспетчер» — ИИ-помощник, «Заказы» — создание заказа и поездка, «Поездки» — журнал всех заказов, «Кабинет» — профиль, роли и повтор обучения. Тур можно пройти заново: Кабинет → «Обучение».",
    },
    {
      target: '[data-tour="tab-dispatcher"]',
      advance: "tap",
      text: "Начнём с «Диспетчера». Откройте вкладку — это личный чат с ИИ-помощником.",
    },
    {
      target: '[data-tour="ai-composer"]',
      advance: "next",
      text: "ИИ-диспетчер понимает обычные слова: «Нужна машина от ЖД до Центра», «Где моя машина?», «Отмени заказ». Внизу — поле ввода, микрофон для голоса и кнопка отправки, выше — готовые подсказки. Во время обучения реальные заказы через ИИ отключены, поэтому попробуем безопасный диалог.",
    },
    {
      target: '[data-tour="ai-input"]',
      advance: "input",
      minLength: 3,
      text: "Наберите в поле слово «Привет» — так вы проверите, что диспетчер на связи.",
    },
    {
      target: '[data-tour="ai-send"]',
      advance: "tap",
      orGone: true,
      blurOnNext: true,
      text: "Отправьте сообщение кнопкой ➤ или клавишей Enter.",
    },
    {
      target: '[data-tour="ai-thread"]',
      advance: "next",
      blurOnNext: true,
      text: "Диспетчер ответил в этом же чате. В обычном режиме он оформляет заказ, показывает отклики водителей, статус поездки и принимает команды голосом. Теперь перейдём к заказу.",
    },
    {
      target: '[data-tour="tab-taxi"]',
      advance: "tap",
      text: "Откройте «Заказы» — здесь создаётся поездка и проходит вся работа с водителем.",
    },
    {
      target: '[data-tour="passenger-subtabs"]',
      advance: "next",
      text: "Внутри пять разделов: «Заказ» — новая поездка, «Мои заказы» — активные и все прошлые, «Водители» — свободные машины города с кнопкой звонка, «Избранные» — сохранённые водители, «Диалоги» — личные чаты.",
    },
    {
      target: '[data-tour="passenger-new-order"]',
      advance: "tap",
      text: "Нажмите «Новый заказ», чтобы открыть форму.",
    },
    {
      target: '[data-tour="passenger-route"]',
      advance: "input",
      minLength: 2,
      text: "Напишите маршрут одной строкой. Например: от ЖД до Центра. Начните вводить адрес — появятся подсказки.",
    },
    {
      target: '[data-tour="passenger-when"]',
      advance: "next",
      blurOnNext: true,
      text: "В форме можно выбрать «⚡ Сейчас» или «🕓 На время» и указать время подачи. В поле ниже — комментарий водителю, например «у подъезда №3». Для учебного заказа достаточно маршрута.",
    },
    {
      target: '[data-tour="passenger-send"]',
      advance: "tap",
      orGone: true,
      text: "Отправьте заказ жёлтой кнопкой. Учебный водитель Дмитрий сразу пришлёт отклик с ценой и временем подачи.",
    },
    {
      target: '[data-tour="offer-accept"]',
      advance: "tap",
      orGone: true,
      text: "В отклике видно имя водителя, цену и время подачи. Сравнивайте отклики и нажмите «Принять» у подходящего.",
    },
    {
      target: '[data-tour="chat-input"]',
      advance: "input",
      minLength: 1,
      text: "Открылся чат заказа: здесь переписка с водителем и кнопка звонка. Напишите, например, «Здравствуйте».",
    },
    {
      target: '[data-tour="chat-send"]',
      advance: "tap",
      blurOnNext: true,
      text: "Отправьте сообщение кнопкой со стрелкой.",
    },
    {
      target: '[data-tour="order-action"]',
      matchText: "Жду у подъезда",
      advance: "tap",
      unless: { target: '[data-tour="order-status"]', await: /в пути|завершён/i },
      text: "Следите за статусом в шапке заказа. Когда машина на месте, нажмите «Жду у подъезда».",
    },
    {
      target: '[data-tour="order-status"]',
      advance: "text",
      await: /завершён/i,
      text: "Водитель начал поездку — статус в шапке обновляется автоматически. Дождитесь завершения: как только поездка закончится, тур предложит оценить водителя.",
    },
    {
      target: '[data-tour="rating-stars"]',
      advance: "tap",
      holdMs: 900,
      text: "Поездка завершена! Поставьте оценку водителю — нажмите на звезду. Оценка влияет на его рейтинг.",
    },
    {
      target: '[data-tour="tab-trips"]',
      advance: "tap",
      text: "Дальше — «Поездки». Откройте журнал.",
    },
    {
      target: '[data-tour="trips-filter"]',
      advance: "next",
      text: "Здесь все ваши заказы: «Текущие» и «Прошлые». Нажмите на поездку, чтобы открыть её чат и детали. Учебная поездка уже в списке.",
    },
    {
      target: '[data-tour="tab-cabinet"]',
      advance: "tap",
      text: "Последняя вкладка — «Кабинет». Откройте её.",
    },
    {
      target: '[data-tour="cab-summary"]',
      advance: "next",
      text: "В кабинете: профиль и телефон, «Мои роли» со статусами, анкета водителя, заявки на другие роли, город и выход. Всё меняется здесь.",
    },
    {
      target: '[data-tour="cab-training"]',
      advance: "tap",
      text: "Найдите раздел «Обучение» и нажмите на него — именно здесь тур запускается заново.",
    },
    {
      target: '[data-tour="cab-training-replay"]',
      advance: "next",
      text: "Кнопка «Обучение пассажира» запускает тур в любой момент. Прогресс сохраняется, а учебные заказы реальным водителям и пассажирам не видны.",
    },
    {
      advance: "next",
      text: "Обучение завершено! Повторить: Кабинет → «Обучение». Создать поездку: «Заказы» → «Новый заказ». Помощь: «Диспетчер». Хорошей дороги!",
    },
  ],
  driver: [
    {
      target: '[data-tour="context-row"]',
      advance: "next",
      text: "Добро пожаловать! Сверху — город, роль и уведомления. Обучение идёт на учебном заказе: его видите только вы, реальная лента и пассажиры его не получают.",
    },
    {
      target: '[data-tour="tabbar"]',
      advance: "next",
      text: "Четыре вкладки: «Диспетчер» — ИИ-помощник, «Заказы» — смена, лента и отклики, «Поездки» — журнал, «Кабинет» — профиль и повтор обучения. Тур можно запустить заново: Кабинет → «Обучение».",
    },
    {
      target: '[data-tour="tab-dispatcher"]',
      advance: "tap",
      text: "Начнём с ИИ-диспетчера. Откройте вкладку.",
    },
    {
      target: '[data-tour="ai-composer"]',
      advance: "next",
      text: "Диспетчер понимает обычные фразы: «На линии», «Доступные заказы», «Мои заказы». Внизу — поле, микрофон и кнопка отправки, выше — подсказки. В обучении смена и реальные заказы через чат отключены.",
    },
    {
      target: '[data-tour="ai-input"]',
      advance: "input",
      minLength: 3,
      text: "Наберите «Привет» — проверим, что диспетчер отвечает.",
    },
    {
      target: '[data-tour="ai-send"]',
      advance: "tap",
      orGone: true,
      blurOnNext: true,
      text: "Отправьте сообщение кнопкой ➤.",
    },
    {
      target: '[data-tour="ai-thread"]',
      advance: "next",
      blurOnNext: true,
      text: "Ответ пришёл в чат. В обычном режиме здесь же можно встать на линию, взять заказ, открыть карточку и написать пассажиру. Переходим к учебному заказу.",
    },
    {
      target: '[data-tour="tab-taxi"]',
      advance: "tap",
      text: "Откройте «Заказы» — это рабочее место водителя.",
    },
    {
      target: '[data-tour="driver-shift"]',
      advance: "next",
      text: "Зелёная кнопка включает смену: на линии вы получаете заказы, без линии лента молчит. Сервер снимает с линии, если телефон молчит 90 секунд. В обучении оставайтесь как есть — учебный заказ уже в ленте.",
    },
    {
      target: '[data-tour="driver-feed"]',
      advance: "next",
      text: "Выше — «Моя поездка» с кнопками статусов и чатом, «Мои брони» для заказов на время, ниже «Лента заказов» города и «Диалоги». Сейчас в ленте учебный заказ Анны.",
    },
    {
      target: '[data-tour="driver-offer"]',
      advance: "tap",
      text: "В реальном режиме заказ не «принимают», а отправляют отклик: свою цену и время подачи. Нажмите «Прислать отклик» у учебного заказа.",
    },
    {
      target: '[data-tour="dialog-input"]',
      advance: "input",
      minLength: 3,
      text: "Введите цену поездки — например, 300. Пассажир увидит её в отклике.",
    },
    {
      target: '[data-tour="dialog-ok"]',
      advance: "tap",
      orGone: true,
      text: "Нажмите «Отправить», чтобы перейти к времени подачи.",
    },
    {
      target: '[data-tour="dialog-input"]',
      advance: "input",
      text: "Укажите, за сколько минут подадите машину — например, 5.",
    },
    {
      target: '[data-tour="dialog-ok"]',
      advance: "tap",
      orGone: true,
      blurOnNext: true,
      text: "Отправьте отклик. Теперь слово за пассажиром.",
    },
    {
      target: '[data-tour="driver-open-chat"]',
      advance: "tap",
      text: "Анна принимает отклик — заказ становится вашим. В карточке «Моя поездка» нажмите «Чат».",
    },
    {
      target: '[data-tour="chat-input"]',
      advance: "input",
      minLength: 1,
      text: "Напишите пассажирке, например: «Здравствуйте, буду через 5 минут».",
    },
    {
      target: '[data-tour="chat-send"]',
      advance: "tap",
      blurOnNext: true,
      text: "Отправьте сообщение кнопкой ➤.",
    },
    {
      target: '[data-tour="order-action"]',
      matchText: "Выехал к вам",
      advance: "tap",
      text: "Отправляясь к пассажирке, нажмите «Выехал к вам» — она увидит статус.",
    },
    {
      target: '[data-tour="order-action"]',
      matchText: "Я на месте",
      advance: "tap",
      text: "Приехали? Отметьте «Я на месте» — включится ожидание.",
    },
    {
      target: '[data-tour="order-action"]',
      matchText: "Поехали",
      advance: "tap",
      text: "Пассажирка села — начинайте поездку кнопкой «Поехали».",
    },
    {
      target: '[data-tour="order-action"]',
      matchText: "Завершить поездку",
      advance: "tap",
      text: "На месте? Завершите поездку — она уйдёт в журнал.",
    },
    {
      target: '[data-tour="rating-stars"]',
      advance: "tap",
      holdMs: 900,
      text: "Поставьте оценку пассажирке — нажмите на звезду.",
    },
    {
      target: '[data-tour="tab-trips"]',
      advance: "tap",
      text: "Откройте «Поездки» — журнал выполненных заказов.",
    },
    {
      target: '[data-tour="trips-filter"]',
      advance: "next",
      text: "«Текущие» и «Прошлые»: здесь и учебная поездка. Нажмите на поездку, чтобы открыть чат и детали, например цену и время.",
    },
    {
      target: '[data-tour="tab-cabinet"]',
      advance: "tap",
      text: "Остался «Кабинет». Откройте его.",
    },
    {
      target: '[data-tour="cab-summary"]',
      advance: "next",
      text: "В кабинете: профиль, «Мои роли», карточка водителя с машиной и рейтингом, заявки, город, выход.",
    },
    {
      target: '[data-tour="cab-training"]',
      advance: "tap",
      text: "Раздел «Обучение» — здесь тур можно пройти заново. Нажмите на него.",
    },
    {
      target: '[data-tour="cab-training-replay"]',
      advance: "next",
      text: "Кнопка «Обучение водителя» запускает тур в любой момент. Учебные заказы видят только вы — реальные пассажиры их не получают.",
    },
    {
      advance: "next",
      text: "Обучение завершено! Повторить: Кабинет → «Обучение». Заказы: вкладка «Заказы». Помощь: «Диспетчер». Удачной смены!",
    },
  ],
};
